import type { IDatasource, IGetRowsParams } from "ag-grid-community";
import type { VariablesOf } from "gql.tada";
import type {
  BroadcastClip,
  BroadcastClipOrdering,
  FetchBroadcastClipsFilters,
} from "@/data-access/discovery-view/queries/discovery-view-queries";
import { GetBroadcastClips } from "@/data-access/discovery-view/queries/discovery-view-queries";
import { client } from "@/lib/urqlProvider";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import type { BroadcastClipRowData } from "./BroadcastClipsTableStates";

// Re-export so consumers (e.g. BroadcastClipsTable) can import the type from here.
export type { BroadcastClipOrdering };

// Constants
const DEFAULT_PAGE_SIZE = 50;

type BroadcastClipsQueryFilters = VariablesOf<
  typeof GetBroadcastClips
>["filters"];

/**
 * Fetches a page of broadcast clips from the GraphQL API.
 * Private to this module — store state updates are the datasource's
 * responsibility, matching the pattern in feedArticlesTableDatasource.
 */
const fetchBroadcastClipsPage = async (
  filters: FetchBroadcastClipsFilters | undefined,
  ordering: BroadcastClipOrdering | undefined,
  limit: number,
  offset: number,
): Promise<{ clips: BroadcastClip[]; totalCount: number }> => {
  const gqlFilters: BroadcastClipsQueryFilters = filters
    ? {
        ...filters,
        keyMessageIds:
          filters.keyMessageIds && filters.keyMessageIds.length > 0
            ? filters.keyMessageIds
            : undefined,
      }
    : undefined;

  const result = await client
    .query(
      GetBroadcastClips,
      {
        filters: gqlFilters,
        ordering: ordering ? [ordering] : [],
        pagination: { limit, offset },
      },
      {
        // network-only ensures each AG Grid block request fetches fresh data
        // and avoids URQL cache interfering with AG Grid's own block cache.
        requestPolicy: "network-only",
      },
    )
    .toPromise();

  if (result.error) {
    throw new Error(result.error.message);
  }

  const data = result.data?.broadcastClips;

  if (!data) {
    return { clips: [], totalCount: 0 };
  }

  return {
    clips: (data.results ?? []) as BroadcastClip[],
    totalCount: data.totalCount ?? 0,
  };
};

/**
 * Transforms a GraphQL BroadcastClip result into a flat AG Grid row object.
 */
const transformClipToRowData = (clip: BroadcastClip): BroadcastClipRowData => ({
  ID: clip.id,
  TITLE: clip.title ?? null,
  TRANSCRIPT_EXCERPT: clip.transcriptExcerpt ?? null,
  AIR_DATETIME: clip.airDatetimeUtc ?? null,
  DURATION: clip.durationFormatted ?? null,
  TOTAL_AUDIENCE: clip.totalAudience ?? null,
  TOTAL_AD_VALUE: clip.totalAdValue ?? null,
  NATIONAL_AUDIENCE: clip.nationalAudience ?? null,
  NATIONAL_AD_VALUE: clip.nationalAdValue ?? null,
  NATIONAL_AD_VALUE_CENTS: clip.nationalAdValueCents ?? null,
  LOCAL_AUDIENCE: clip.localAudience ?? null,
  LOCAL_AD_VALUE: clip.localAdValue ?? null,
  LOCAL_AD_VALUE_CENTS: clip.localAdValueCents ?? null,
  LOCAL_AD_RATE: clip.localAdRate ?? null,
  STATION_NAME: clip.station?.name ?? null,
  STATION_NETWORK: clip.station?.network ?? null,
  STATION_MEDIA_TYPE: clip.station?.mediaType ?? null,
  STATION_PRIMARY_LANGUAGE: clip.station?.primaryLanguage ?? null,
  STATION_LOCATION_DISPLAY: clip.station?.locationDisplay ?? null,
  PROGRAM_TITLE: clip.program?.title ?? null,
  PROGRAM_CATEGORY: clip.program?.category ?? null,
  PROGRAM_TYPE: clip.program?.programType ?? null,
});

/**
 * Session tracking — mirrors the pattern used by feedArticlesTableDatasource.
 * Incrementing the session id on each datasource creation ensures that
 * in-flight requests from a previous datasource do not mutate the loading
 * state once a new session has started.
 */
let currentSessionId = 0;
let inFlightCountForCurrentSession = 0;
let latestFetchId = 0;

/**
 * Resets all module-level session state back to zero.
 * Intended for use in tests to prevent state leaking between test runs.
 */
export const resetBroadcastClipsDatasourceSession = () => {
  currentSessionId = 0;
  inFlightCountForCurrentSession = 0;
  latestFetchId = 0;
};

/**
 * Call when a new datasource is created (e.g. filters or ordering changed)
 * so that responses from the previous datasource are ignored.
 */
export const startNewBroadcastClipsDatasourceSession = () => {
  currentSessionId += 1;
  inFlightCountForCurrentSession = 0;
};

/**
 * Creates the AG Grid Infinite Row Model datasource for the broadcast clips table.
 *
 * Reads and writes loading/error state via {@link useDiscoveryContentManagementStore},
 * consistent with the unified discovery store architecture used by the articles
 * and narrative clusters tables.
 *
 * Accepts getter functions for filters and ordering so the closure always reads
 * the latest values at fetch time without needing to be recreated.
 *
 * @param getFilters - Returns current {@link FetchBroadcastClipsFilters}
 * @param getOrdering - Returns current sort ordering (or undefined for default)
 */
export const createBroadcastClipsDatasource = (
  getFilters: () => FetchBroadcastClipsFilters,
  getOrdering: () => BroadcastClipOrdering | undefined,
): IDatasource => {
  return {
    getRows: async (params: IGetRowsParams) => {
      const sessionId = currentSessionId;
      const fetchId = ++latestFetchId;
      inFlightCountForCurrentSession += 1;

      const {
        setIsFetchingBroadcastClips,
        setBroadcastClipsError,
        setTotalBroadcastClipsCount,
      } = useDiscoveryContentManagementStore.getState();

      const filters = getFilters();
      const ordering = getOrdering();

      // Calculate pagination from AG Grid request
      const startRow = params.startRow ?? 0;
      const endRow = params.endRow ?? 0;
      const limit = endRow > startRow ? endRow - startRow : DEFAULT_PAGE_SIZE;
      const offset = startRow;

      try {
        setIsFetchingBroadcastClips(true);
        setBroadcastClipsError(null);

        const { clips, totalCount } = await fetchBroadcastClipsPage(
          filters,
          ordering,
          limit,
          offset,
        );

        const rowData: BroadcastClipRowData[] = clips.map(
          transformClipToRowData,
        );

        // Stale response: still tell the grid about the rows but don't mutate
        // store loading state so the new session's spinner isn't cleared early.
        if (fetchId !== latestFetchId) {
          if (sessionId === currentSessionId) {
            inFlightCountForCurrentSession -= 1;
            if (inFlightCountForCurrentSession === 0) {
              setIsFetchingBroadcastClips(false);
            }
          }
          params.successCallback(rowData, totalCount);
          return;
        }

        setTotalBroadcastClipsCount(totalCount);

        if (sessionId === currentSessionId) {
          inFlightCountForCurrentSession -= 1;
          if (inFlightCountForCurrentSession === 0) {
            setIsFetchingBroadcastClips(false);
          }
        }

        params.successCallback(rowData, totalCount);
      } catch (error: unknown) {
        if (sessionId === currentSessionId) {
          inFlightCountForCurrentSession -= 1;
          if (inFlightCountForCurrentSession === 0) {
            setIsFetchingBroadcastClips(false);
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            setBroadcastClipsError(errorMessage);
          }
        }
        params.failCallback();
      }
    },
  };
};
